import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const industries = ["Healthcare", "Manufacturing", "Financial Services", "Legal", "Technology", "Other"];

const inputCls =
  "w-full bg-arq-surface1 border border-arq-border text-arq-text placeholder:text-arq-tertiary rounded-md px-4 py-[14px] text-[15px] focus:border-arq-blue focus:outline-none focus:ring-2 focus:ring-arq-blue/20 transition-colors";

export default function ArqBooking() {
  const { toast } = useToast();
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    industry: "",
    message: "",
    honeypot: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        fullName: `${form.firstName} ${form.lastName}`.trim(),
        email: form.email,
        phone: "",
        companyName: form.company,
        revenue: "",
        challenge: form.message,
        timeToConnect: `Industry: ${form.industry}`,
        honeypot: form.honeypot,
      };
      const { error } = await supabase.functions.invoke("send-booking-email", { body: payload });
      if (error) throw error;
      toast({
        title: "Request received",
        description: "We will respond with a written proposal within 48 hours.",
      });
      setForm({
        firstName: "", lastName: "", email: "", company: "",
        industry: "", message: "", honeypot: "",
      });
    } catch (err) {
      console.error(err);
      toast({ title: "Something went wrong", description: "Please try again.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="arq-radial-br py-20 md:py-[120px] px-6">
      <div className="max-w-[640px] mx-auto">
        {/* Pull quote */}
        <blockquote className="mb-16 text-center">
          <p
            className="font-display font-medium text-arq-text leading-[1.45] mb-6"
            style={{ fontSize: "clamp(20px, 2.4vw, 26px)" }}
          >
            "We deployed an AI scribe across 12 clinics and had no idea what it was actually doing with patient data. ArqeraAI found three critical exposures in the first assessment. The board presentation paid for the engagement on its own."
          </p>
          <footer className="text-arq-muted text-[14px]">
            — Chief Compliance Officer, Regional Health System (engagement NDA)
          </footer>
        </blockquote>

        <div className="text-center mb-12">
          <h2 className="font-display font-semibold text-arq-text mb-5" style={{ fontSize: "clamp(28px, 4vw, 40px)", lineHeight: 1.2 }}>
            Start With a Conversation.
          </h2>
          <p className="text-arq-muted text-[18px] leading-[1.6] max-w-[560px] mx-auto">
            45 minutes. We will assess your environment, identify your highest-risk exposure, and
            tell you exactly what a review would cover. No commitment. Written proposal within 48
            hours if you want to proceed.
          </p>
        </div>

        <form onSubmit={onSubmit} className="arq-card p-8 md:p-12 space-y-5">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-[13px] text-arq-muted mb-2 font-medium">First name</label>
              <input required className={inputCls} value={form.firstName} onChange={(e) => set("firstName", e.target.value)} />
            </div>
            <div>
              <label className="block text-[13px] text-arq-muted mb-2 font-medium">Last name</label>
              <input required className={inputCls} value={form.lastName} onChange={(e) => set("lastName", e.target.value)} />
            </div>
          </div>

          <div>
            <label className="block text-[13px] text-arq-muted mb-2 font-medium">Work email</label>
            <input type="email" required className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} />
          </div>

          <div>
            <label className="block text-[13px] text-arq-muted mb-2 font-medium">Company name</label>
            <input required className={inputCls} value={form.company} onChange={(e) => set("company", e.target.value)} />
          </div>

          <div>
            <label className="block text-[13px] text-arq-muted mb-2 font-medium">Industry</label>
            <select required className={inputCls} value={form.industry} onChange={(e) => set("industry", e.target.value)}>
              <option value="">Select…</option>
              {industries.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-[13px] text-arq-muted mb-2 font-medium">Message <span className="text-arq-tertiary">(optional)</span></label>
            <textarea rows={4} className={inputCls} value={form.message} onChange={(e) => set("message", e.target.value)} />
          </div>

          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={form.honeypot}
            onChange={(e) => set("honeypot", e.target.value)}
            style={{ position: "absolute", left: "-9999px", width: 1, height: 1 }}
            aria-hidden="true"
          />

          <p className="text-center text-[13px] text-arq-muted mb-4">
            Trusted by compliance, security, and engineering teams at regulated companies.
          </p>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-arq-blue text-white font-semibold text-[16px] py-[18px] rounded-md hover:bg-arq-blue-bright hover:shadow-[0_0_24px_rgba(26,111,212,0.35)] transition-all duration-150 disabled:opacity-60"
          >
            {loading ? "Sending…" : "Schedule My Discovery Call"}
          </button>

          <p className="text-center text-[13px] text-arq-tertiary mt-4">
            No sales pitch. No commitment. Written proposal within 48 hours if you want to proceed.
          </p>
        </form>
      </div>
    </section>
  );
}
